import React from "react";
import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useOrgDeviceTypeOptions from "hooks/organizationDeviceTypes/useOrgDeviceTypeOptions";
import { Asset } from "types/asset";
import { Employee } from "types/employee";
import { Location } from "types/location";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
  },
};

export type AssetFormValues = {
  serialNumber: string;
  model: string;
  manufacturer: string;
  deviceType: string;
  status: string;
  condition: string;
  assetTag: string;
  unitNumber: string;
  quantity: string;
  notes: string;
  purchaseDate: string;
  warrantyExpiresOn: string;
  cost: string;
  locationId: string;
  employeeId: string;
};

export function emptyAssetFormValues(): AssetFormValues {
  return {
    serialNumber: "",
    model: "",
    manufacturer: "",
    deviceType: "",
    status: "Available",
    condition: "",
    assetTag: "",
    unitNumber: "",
    quantity: "",
    notes: "",
    purchaseDate: "",
    warrantyExpiresOn: "",
    cost: "",
    locationId: "",
    employeeId: "",
  };
}

export function assetToFormValues(a: Asset): AssetFormValues {
  return {
    serialNumber: a.serialNumber || "",
    model: a.model || "",
    manufacturer: a.manufacturer || "",
    deviceType: a.deviceType || "",
    status: a.status || "Available",
    condition: a.condition || "",
    assetTag: a.assetTag || "",
    unitNumber: a.unitNumber || "",
    quantity: a.quantity != null ? String(a.quantity) : "",
    notes: a.notes || "",
    purchaseDate: a.purchaseDate ? a.purchaseDate.slice(0, 10) : "",
    warrantyExpiresOn: a.warrantyExpiresOn ? a.warrantyExpiresOn.slice(0, 10) : "",
    cost: a.cost != null && a.cost !== "" ? String(a.cost) : "",
    locationId: a.location?.id != null ? String(a.location.id) : "",
    employeeId: a.employee?.id != null ? String(a.employee.id) : "",
  };
}

/** Empty clears cost. Invalid input: omit (keeps existing on PATCH). */
export function costFromFormForUpdate(raw: string): { cost: number | null } | Record<string, never> {
  const t = raw.trim();
  if (!t) return { cost: null };
  const n = Number(t);
  if (!Number.isFinite(n)) return {};
  return { cost: n };
}

/** Empty or invalid: omit. */
export function costFromFormForCreate(raw: string): { cost: number } | Record<string, never> {
  const t = raw.trim();
  if (!t) return {};
  const n = Number(t);
  if (!Number.isFinite(n)) return {};
  return { cost: n };
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>
        {children}
      </Typography>
      <Divider sx={{ mb: 2 }} />
    </Grid>
  );
}

type AssetFormProps = {
  values: AssetFormValues;
  setValues: React.Dispatch<React.SetStateAction<AssetFormValues>>;
  locations: Location[];
  employees: Employee[];
};

export default function AssetForm({ values, setValues, locations, employees }: AssetFormProps) {
  const { options: deviceTypeOptions, loading: deviceTypeOptionsLoading } = useOrgDeviceTypeOptions();
  const set =
    (field: keyof AssetFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  return (
    <Grid container spacing={2}>
      <SectionTitle>Identification</SectionTitle>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Serial number"
          value={values.serialNumber}
          onChange={set("serialNumber")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Asset tag"
          value={values.assetTag}
          onChange={set("assetTag")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Manufacturer"
          value={values.manufacturer}
          onChange={set("manufacturer")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Model" value={values.model} onChange={set("model")} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          freeSolo
          options={deviceTypeOptions}
          loading={deviceTypeOptionsLoading}
          value={values.deviceType}
          onInputChange={(_e, v) => setValues((prev) => ({ ...prev, deviceType: v }))}
          onChange={(_e, v) => {
            const val = typeof v === "string" ? v : v || "";
            setValues((prev) => ({ ...prev, deviceType: val }));
          }}
          renderInput={(params) => (
            <TextField {...params} fullWidth label="Device type" sx={fieldSx} />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Unit number"
          value={values.unitNumber}
          onChange={set("unitNumber")}
          sx={fieldSx}
        />
      </Grid>

      <SectionTitle>Status and quantity</SectionTitle>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Status" value={values.status} onChange={set("status")} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Condition"
          value={values.condition}
          onChange={set("condition")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Quantity"
          value={values.quantity}
          onChange={set("quantity")}
          sx={fieldSx}
        />
      </Grid>

      <SectionTitle>Dates</SectionTitle>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Purchase date"
          InputLabelProps={{ shrink: true }}
          value={values.purchaseDate}
          onChange={set("purchaseDate")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Warranty expires"
          InputLabelProps={{ shrink: true }}
          value={values.warrantyExpiresOn}
          onChange={set("warrantyExpiresOn")}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Cost"
          value={values.cost}
          onChange={set("cost")}
          inputProps={{ inputMode: "decimal" }}
          sx={fieldSx}
        />
      </Grid>

      <SectionTitle>Assignment</SectionTitle>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="asset-form-loc">Location</InputLabel>
          <Select
            labelId="asset-form-loc"
            label="Location"
            value={values.locationId}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, locationId: e.target.value as string }))
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.uuid || loc.id} value={String(loc.id)}>
                {loc.name || loc.address}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="asset-form-emp">Assigned employee</InputLabel>
          <Select
            labelId="asset-form-emp"
            label="Assigned employee"
            value={values.employeeId}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, employeeId: e.target.value as string }))
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.uuid} value={String(emp.id)}>
                {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <SectionTitle>Notes</SectionTitle>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Notes"
          value={values.notes}
          onChange={set("notes")}
          sx={fieldSx}
        />
      </Grid>
    </Grid>
  );
}
